import { InfoDelta } from "$root/components/InfoDelta";
import { MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { getInvestmentHorizonSetup } from "$root/components/Portfolio/common_hooks/useInvestmentHorizonSetup";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import UserShareIcon from "$root/functional-areas/acl/UserShareIcon";
import { aclToUsersListEntries, UsersList } from "$root/functional-areas/acl/UsersList";
import type { MinimumCompareProps } from "$root/functional-areas/compare-portfolio/MinimumCompareProps";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { formatWeightTotal } from "$root/functional-areas/instruments-editor/weight-total";
import { spawnInstrumentClassificationDialog } from "$root/functional-areas/instruments/instrument-classification/InstrumentClassificationDialog";
import { mainMenuItemsForUserClassification } from "$root/functional-areas/instruments/Instrument-customisation/menu-items";
import { spawnInstrumentCustomizationSidePanel } from "$root/functional-areas/instruments/Instrument-customisation/SidePanelColumnsAdder";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { currencyCode } from "$root/utils/currency";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { AutoSyncStatus } from "$root/functional-areas/portfolio/AutoSyncStatus";
import type { ReviewGridRow, ReviewInstrument } from "$root/functional-areas/proxies/const";
import { customerDecimalPlaces, user$ } from "$root/functional-areas/user";
import type { WorkspaceDataCast } from "$root/functional-areas/workspace/WorkspaceDataDefinitionCast";
import {
	type FormatNumberOptions,
	formatDate,
	formatNumber,
	formatNumberWithBankersRounding,
} from "$root/localization/formatters";
import { PortfolioDetailsTabs } from "$root/pages/PortfolioDetails/portfolio-details-tabs";
import type { ReferenceItem } from "$root/pages/PortfoliosStudio/common";
import { LinkedPortfolioCounterWithTooltip } from "$root/pages/PortfoliosStudio/shared";
import { statusIconMap } from "$root/pages/PortfoliosStudio/statusIconMap";
import { getPortfolioStudioUrl } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";
import { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import { AgGridTooltipWrapper, columnChooserMenuItemDef } from "$root/third-party-integrations/ag-grid/ag-grid";
import { builtInCaseInsensitiveSort, coalesce, sumArrayLike } from "$root/utils";
import { truncateToPrecision } from "$root/utils/monetary-math";
import type { MandatoryItemData, MandatoryItemDataRichAcl } from "@eu.mdotm.api/smart-workspaces";
import {
	type Currencies,
	type Description,
	FieldKey,
	type ForecastHorizons,
	type ImportedPerformances,
	type IndexTicker,
	type InvestableUniverseSelectionStrategy,
	type InvestmentActions,
	type InvestmentExposureEntry,
	type InvestmentNote,
	type InvestmentPortfolioComparisonDetailsSummary,
	type InvestmentStatus,
	type MarketViewListEntry,
	type MarketViewMonitoringMetric,
	type MarketViewSettings,
	type ReferenceUniverseListEntry,
	type ReviewTickerIssueEnum,
	type RichAcl,
	type RichRiskModelValue,
	type TagValue,
	type UserInstrumentClassificationDto,
	type UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import { ActionText, Badge, Icon, IconTooltip, Row, Text } from "@mdotm/shadcn/legacy/components";
import { readAtom, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	builtInSort,
	builtInSortFnFor,
	instanceEqualsOrFastDeepEqual,
	noop,
	switchExpr,
} from "@mdotm/shadcn/legacy/utils";
import { Result } from "@mdotm/utils/runtime";
import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import type { ColDef, ColDefField, HeaderValueGetterParams, ValueFormatterParams } from "ag-grid-community";
import type { CustomCellRendererProps, CustomTooltipProps } from "ag-grid-react";
import { differenceInDays } from "date-fns";
import { t } from "i18next";
import { renderToStaticMarkup } from "react-dom/server";
import { match } from "ts-pattern";
import z from "zod";
import { isRowBlocked } from "../custom-column/instrument/blocking-issue";
import { instrumentIssueColdDef, spawnUnrecognizedInstrumentDialog } from "../custom-column/instrument/issue-cell";
import InvestmentName, { type RouteProps } from "../custom-column/investment/InvestmentName";
import InvestmentTooltip from "../custom-column/investment/InvestmentTooltip";
import { settingsColdDef } from "../custom-column/setting/base-cell";
import { type TagSettings, tagColDef } from "../custom-column/tags/base-cell";
import { richTagColDef } from "../custom-column/tags/rich-tag-cell";
import { riskModelColDef } from "../custom-column/tags/risk-model-cell";
import { tagWeightColDef } from "../custom-column/tags/tag-weight-cell";
import { type ColumnOverrideParams, commonColumnsDef } from "./common-columns-definition";

function actionColumnsByArea() {
	const settingDef = settingsColdDef();
	return {
		instrumentCustomisation<T>(opts?: { onExport?: () => void }): ColDef<T> {
			const MAX_CUSTOM_COLUMNS = 40;
			const classifications = readAtom(userClassifications$);
			return {
				headerComponent: settingDef.columnMenuChooserWithLoading,
				mainMenuItems() {
					return [
						{
							name: "Add new column",
							icon: renderToStaticMarkup(<Icon icon="Outline1" size={16} />),
							action() {
								spawnInstrumentCustomizationSidePanel({
									mode: "creation",
									onSubmitAsync: instrumentsRepository.createClassificationAndMakeVisible,
								}).catch(noop);
							},
							disabled: (classifications?.length ?? 0) > MAX_CUSTOM_COLUMNS,
						},
						columnChooserMenuItemDef,
						...(opts?.onExport
							? [
									"separator" as const,
									{
										name: "Export",
										icon: renderToStaticMarkup(<Icon icon="Dowload" size={16} />),
										action: opts.onExport,
									},
								]
							: []),
					];
				},
				cellRenderer: settingDef.contextMenuTrigger,
			};
		},
		instrumentCompositionEdit<T extends { deleted?: boolean }>(): ColDef<T> {
			return {
				field: "deleted" as any,
				headerComponent: settingDef.columnChooser,
				cellRenderer(params: CustomCellRendererProps<T>) {
					if (params.node.group) {
						return "";
					}

					return settingDef.deleteButton(params);
				},
				onCellClicked(params) {
					if (params.node.group) {
						return;
					}

					const columnId = params.column.getColId();
					if (columnId) {
						params.node.setDataValue(columnId, !params.value);
					}
				},
			};
		},
		/** Quantity-mode variant of {@link instrumentCompositionEdit}: the cash row is not deletable. */
		instrumentQuantityCompositionEdit<T extends { deleted?: boolean; isCash?: boolean }>(): ColDef<T> {
			return {
				field: "deleted" as any,
				headerComponent: settingDef.columnChooser,
				cellRenderer(params: CustomCellRendererProps<T>) {
					if (params.node.group || params.data?.isCash) {
						return "";
					}

					return settingDef.deleteButton(params);
				},
				onCellClicked(params) {
					if (params.node.group || params.data?.isCash) {
						return;
					}

					const columnId = params.column.getColId();
					if (columnId) {
						params.node.setDataValue(columnId, !params.value);
					}
				},
			};
		},
		instrumentCompositionRead<T>(): ColDef<T> {
			return {
				headerComponent: settingDef.columnChooser,
				cellRenderer: () => "",
			};
		},
		instrumentReview<T extends { issue?: ReviewTickerIssueEnum; delisted?: boolean; deleted?: boolean }>(): ColDef<T> {
			return {
				field: "deleted" as any,
				headerComponent: settingDef.columnChooser,
				cellRenderer(params: CustomCellRendererProps<T>) {
					if (params.data && isRowBlocked(params.data)) {
						return "";
					}
					return settingDef.deleteButton(params);
				},
				onCellClicked(params) {
					if (params.data && isRowBlocked(params.data)) {
						return;
					}
					const columnId = params.column.getColId();
					if (columnId) {
						params.node.setDataValue(columnId, !params.data?.deleted);
					}
				},
			};
		},
	};
}

/**
 * A numeric field every `Instrument` carries, named on a row type that extends it: `ColDefField` cannot
 * resolve a path on a generic row.
 */
function instrumentField<T extends Instrument>(
	field: ColDefField<Instrument, number | undefined>,
): ColDefField<T, number | undefined> {
	return field as ColDefField<T, number | undefined>;
}

export type InstrumentActionsOverrides<T> = Omit<
	ColumnOverrideParams<T>,
	"cellRenderer" | "suppressHeaderFilterButton" | "sortable"
>;

/** Shared shell of the per-area `instrument*Actions` columns: fixed-width, non-sortable, non-editable. */
function instrumentActionsColDef<T>(areaColDef: ColDef<T>, opt?: InstrumentActionsOverrides<T>): ColDef<T> {
	return {
		suppressHeaderFilterButton: true,
		sortable: false,
		editable: false,
		width: 48,
		minWidth: 48,
		maxWidth: 48,
		...areaColDef,
		...coalesce(opt, {}),
	};
}

export type CompareInvestmentProps = {
	queryConfig(uuid: string): UseQueryOptions<InvestmentPortfolioComparisonDetailsSummary>;
	onCompareInvestments?(params: MinimumCompareProps): void;
};

export const sphereColumnsDef = {
	...commonColumnsDef,
	linkedPortfolios<T extends ReferenceUniverseListEntry>(): ColDef<T> {
		return {
			colId: "LINKED_PORTFOLIOS",
			headerName: "Linked portfolios",
			valueGetter: (row) => row.data,
			cellRenderer(row: CustomCellRendererProps<T>) {
				const { referralInvestments, nofPortfolios } = row.data ?? {};
				return (
					<Row className="h-full w-full" alignItems="center" justifyContent="end">
						<LinkedPortfolioCounterWithTooltip
							type="Universe"
							portfolios={referralInvestments}
							othersCount={(nofPortfolios ?? 0) - (referralInvestments?.length ?? 0)}
						/>
					</Row>
				);
			},
			width: 128,
			comparator: (a: T, b: T) => {
				return builtInSort(a?.nofPortfolios, b?.nofPortfolios);
			},
		};
	},
	nOfInstruments<T extends ReferenceUniverseListEntry>(): ColDef<T, number | undefined> {
		return commonColumnsDef.integer("nofInstruments" satisfies keyof ReferenceUniverseListEntry as any, {
			width: 92,
			colId: "NO_OF_INSTRUMENTS",
			headerName: "Number of instruments",
		});
	},
	universeName<T extends ReferenceUniverseListEntry>(): ColDef<T, string | undefined> {
		return {
			...commonColumnsDef.string("name" satisfies keyof T as any),
			colId: "NAME",
			headerName: "Name",
			pinned: "left",
			width: 320,
			sort: "asc",
			cellRenderer(row: CustomCellRendererProps<T>) {
				const universe = row.data as ReferenceUniverseListEntry;

				function href() {
					if (universe.universeType === "GROUP_UNIVERSE") {
						return typedUrlForRoute("UniverseWithGroupsDetails", { universeUuid: universe.uuid ?? "-" });
					}

					if (universe.status === "REVIEW") {
						return typedUrlForRoute("ReviewInstruments", {
							uuid: universe.uuid ?? "-",
							entity: ReviewEntity.UNIVERSE,
						});
					}

					return typedUrlForRoute("UniverseDetails", { universeUuid: universe.uuid ?? "-" });
				}

				return (
					<Row className="h-full w-full" alignItems="center">
						<ActionText
							className="line-clamp-2 mr-1"
							color={themeCSSVars.palette_N900}
							href={href()}
							title={universe.name}
							data-qualifier="PortfolioStudio/UniverseList/Table/Column(Name)"
						>
							{universe.name}
						</ActionText>
						<WithAtom atom$={user$}>
							{(user) => (
								<UserShareIcon
									entity="UNIVERSE"
									userId={user.id}
									permission={universe?.richAcl?.currentUserPermissions}
									color={themeCSSVars.palette_N300}
									acl={universe?.richAcl?.acl}
								/>
							)}
						</WithAtom>
					</Row>
				);
			},
		};
	},
	universeType<T extends ReferenceUniverseListEntry>(): ColDef<T, string> {
		return {
			headerName: "Type",
			valueGetter: (row) => {
				const universeType = row.data?.universeType;
				return universeType === "GROUP_UNIVERSE" ? "Universe with groups" : "Universe";
			},
			colId: "UNIVERSE_TYPE",
			width: 220,
		};
	},
	colChooserAndCtxMenuTrigger<T>(opts?: ColumnOverrideParams<T, any>): ColDef<T> {
		const settingDef = settingsColdDef();

		return {
			suppressHeaderFilterButton: true,
			suppressColumnsToolPanel: true,
			lockVisible: true,
			sortable: false,
			headerName: "Action",
			colId: "ACTION",
			headerComponent: settingDef.columnChooser,
			cellRenderer: settingDef.contextMenuTrigger,
			width: 48,
			editable: false,
			pinned: "right",
			...(opts ?? {}),
		};
	},
	creationDate<T extends { creationDate?: string }>(): ColDef<T> {
		return commonColumnsDef.date("creationDate" satisfies keyof T as any, {
			colId: "CREATION",
			width: 128,
			headerName: t(`TABLE.HEADERS.INCEPTION_DATE`),
		});
	},
	inceptionDate<T extends { creationTime?: string }>(): ColDef<T> {
		return commonColumnsDef.date("creationTime" satisfies keyof T as any, {
			colId: "INCEPTION_DATE",
			width: 128,
			headerName: t(`TABLE.HEADERS.INCEPTION_DATE`),
		});
	},
	lastStatusUpdate<T extends { modificationTime?: string }>(): ColDef<T> {
		return commonColumnsDef.date("modificationTime" satisfies keyof T as any, {
			colId: "LAST_STATUS_UPDATE",
			headerName: "Last update",
			width: 128,
		});
	},
	marketViewExpiration<T extends { endDate?: string }>(): ColDef<T> {
		return {
			colId: "EXPIRATION",
			headerName: "Expiration date",
			cellRenderer(row: CustomCellRendererProps<T>) {
				const endDate = row.data?.endDate;
				if (!endDate) {
					return "-";
				}
				const expirationDate = new Date(endDate);
				if (differenceInDays(new Date(), expirationDate) > 0) {
					return (
						<Row className="h-full w-full" alignItems="center">
							<div
								className={`bg-[var(--palette-W50)] h-min flex items-center gap-2 py-1 px-2 text-[var(--palette-W500)] rounded-full`}
							>
								<Icon icon="Icon-full-alert" color={themeCSSVars.palette_W500} size={16} />
								<p className="font-semibold leading-none">{formatDate(expirationDate)}</p>
							</div>
						</Row>
					);
				}

				return formatDate(expirationDate);
			},
			comparator: (aOuter, bOuter) => {
				const a = aOuter.endDate;
				const b = bOuter.endDate;
				const rowA = new Date(a ?? "").getTime();
				const rowB = new Date(b ?? "").getTime();
				return rowA > rowB ? 1 : rowA < rowB ? -1 : 0;
			},
			valueFormatter(row) {
				const endDate = row.data?.endDate;
				if (!endDate) {
					return "";
				}
				const expirationDate = new Date(endDate);
				return formatDate(expirationDate);
			},
			width: 192,
		};
	},
	forecastHorizon<T extends { forecastHorizon?: ForecastHorizons }>(): ColDef<T> {
		return {
			colId: "FORECAST_HORIZON",
			headerName: "Forecast Horizon",
			valueGetter: ({ data }) => {
				const forecastHorizon = data?.forecastHorizon;
				if (!forecastHorizon) {
					return "-";
				}
				const forecastHorizonTypography = getInvestmentHorizonSetup().getInvestmentHorizonTypographyFromNewApiValue(
					"multiAsset",
					String(forecastHorizon),
				);
				return forecastHorizonTypography;
			},
			comparator: builtInSortFnFor("forecastHorizon"),
			width: 128,
		};
	},
	marketViewType<T extends MarketViewListEntry>(
		queryMarketViewSettings?: UseQueryResult<MarketViewSettings>,
	): ColDef<T> {
		return {
			width: 172,
			colId: "TYPE",
			headerName: "Type",
			valueGetter(params) {
				const marketViewSettings = queryMarketViewSettings?.data;
				if (params.data?.custom) {
					return marketViewSettings?.customMarketViewName ?? "-";
				}
				return params.data?.type ? String(t(params.data?.type)) : "-";
			},
		};
	},
	sharingWith<T extends { richAcl?: RichAcl }>(area: Parameters<typeof aclToUsersListEntries>[0]): ColDef<T> {
		return {
			colId: FieldKey.SharingViews,
			headerName: "Sharing with",
			field: "richAcl" satisfies keyof T as any,
			cellRenderer(row: CustomCellRendererProps<T>) {
				const users = aclToUsersListEntries(area, row.data?.richAcl?.acl ?? []);
				return (
					<Row className="h-full w-full" alignItems="center">
						<UsersList users={users} visibleSlots={3} size="small" />
					</Row>
				);
			},
			valueFormatter(row) {
				const users = aclToUsersListEntries(area, row.data?.richAcl?.acl ?? []);
				if (users.length === 0) {
					return "";
				}
				return users.map((x) => x.firstName + " " + x.lastName + "(" + x.role + ")").join(" , ");
			},
			comparator: (a, b) => {
				return builtInSort(a?.acl?.length, b?.acl?.length);
			},
			width: 192,
		};
	},
	marketViewName<T extends MarketViewListEntry>(): ColDef<T, string | undefined> {
		return commonColumnsDef.string("name" satisfies keyof T as any, {
			colId: "NAME",
			headerName: "Name",
			width: 320,
			cellRenderer(row: CustomCellRendererProps<T>) {
				const { uuid, name, richAcl, creationDate } = row.data as MarketViewListEntry;
				return (
					<Row className="h-full w-full" alignItems="center">
						<ActionText
							className="line-clamp-2 mr-1"
							color={themeCSSVars.palette_N900}
							href={typedUrlForRoute("MarketViewWorkSpace/View", { uuid: uuid! })}
							title={name}
							data-qualifier="PortfolioStudio/MarketViewList/Table/Column(Name)"
						>
							{!name ? `Market view ${formatDate(creationDate)}` : name}
						</ActionText>
						<WithAtom atom$={user$}>
							{(user) => (
								<UserShareIcon
									entity="MARKET_VIEW"
									userId={user.id}
									permission={richAcl?.currentUserPermissions}
									color={themeCSSVars.palette_N300}
									acl={richAcl?.acl}
								/>
							)}
						</WithAtom>
					</Row>
				);
			},
		});
	},
	tagWeight<T extends UserInstrumentDto = UserInstrumentDto>(
		classification: UserInstrumentClassificationDto,
		opt?: Omit<
			ColumnOverrideParams<T, Array<TagValue>>,
			| "field"
			| "tooltipComponent"
			| "cellRenderer"
			| "cellEditor"
			| "tooltipValueGetter"
			| "valueGetter"
			| "valueSetter"
		>,
	): ColDef<T, Array<TagValue>> {
		return tagWeightColDef(classification, opt);
	},
	tag<T>(
		field: ColDefField<T, Array<string>>,
		meta: TagSettings,
		opt?: ColumnOverrideParams<T, Array<string>>,
	): ColDef<T, Array<string>> {
		return tagColDef(field, meta, opt);
	},
	quality<T extends UserInstrumentDto = UserInstrumentDto>(
		classification: UserInstrumentClassificationDto,
		opt?: Omit<ColumnOverrideParams<T, string | undefined>, "valueGetter" | "valueSetter" | "cellEditor">,
	): ColDef<T, string | undefined> {
		return {
			colId: classification.classificationUuid!,
			headerName: classification.name ?? "-",
			editable: opt?.editable,
			cellEditor: "agLargeTextCellEditor",
			cellEditorPopup: true,
			cellEditorParams: {
				maxLength: 100,
			},
			valueGetter: (row) =>
				row.data?.qualities?.find((q) => q.classificationUuid === classification.classificationUuid)?.value,
			valueSetter: (row) => {
				const field = "qualities" as const;
				const candidate = row.data[field]?.find((s) => s.classificationUuid === classification.classificationUuid);
				if (candidate) {
					candidate.value = coalesce(row.newValue, "");
				} else {
					row.data[field] = (row.data[field] ?? []).concat({
						classificationUuid: classification.classificationUuid,
						standardClassificationId: classification.standardClassificationId,
						value: coalesce(row.newValue, ""),
					});
				}
				return true;
			},
			mainMenuItems: mainMenuItemsForUserClassification(classification),
			keyCreator: (row) =>
				row.data?.qualities?.find((q) => q.classificationUuid === classification.classificationUuid)?.value ?? "",
			comparator(a, b) {
				return builtInCaseInsensitiveSort(a, b);
			},
			...coalesce(opt, {}),
		};
	},
	score<T extends UserInstrumentDto = UserInstrumentDto>(
		classification: UserInstrumentClassificationDto,
		opt?: Omit<ColumnOverrideParams<T, number | null>, "valueGetter" | "valueSetter" | "cellEditor">,
	): ColDef<T, number | null> {
		return {
			colId: classification.classificationUuid!,
			headerName: classification.name ?? "-",
			editable: opt?.editable,
			cellEditor: "agNumberCellEditor",
			valueGetter: (row) =>
				row.data?.scores?.find((s) => s.classificationUuid === classification.classificationUuid)?.value,
			valueSetter: (row) => {
				const field = "scores" as const;
				const candidate = row.data[field]?.find((s) => s.classificationUuid === classification.classificationUuid);
				if (candidate) {
					candidate.value = coalesce(row.newValue, undefined);
				} else {
					row.data[field] = (row.data[field] ?? []).concat({
						classificationUuid: classification.classificationUuid,
						standardClassificationId: classification.standardClassificationId,
						value: coalesce(row.newValue, undefined),
					});
				}
				return true;
			},
			mainMenuItems: mainMenuItemsForUserClassification(classification),
			keyCreator: (row) =>
				String(row.data?.scores?.find((s) => s.classificationUuid === classification.classificationUuid)?.value ?? ""),
			comparator: (a, b) => builtInSort(a, b),
			...coalesce(opt, {}),
		};
	},
	description<T extends { description?: Description }>(
		field: ColDefField<T, string | undefined>,
		opt?: ColumnOverrideParams<T, string | undefined>,
	): ColDef<T, string | undefined> {
		return {
			...commonColumnsDef.markdown(field, opt),
			editable: (row) => row.data?.description?.status !== "CALCULATING",
			valueSetter: (row) => {
				row.data.description = {
					creator: "USER",
					date: new Date().toISOString(),
					status: "COMPLETED",
					value: row.newValue ?? "",
				};
				return true;
			},
			tooltipComponent(e: CustomCellRendererProps<T, string | undefined>): React.JSX.Element | string {
				if (!e.value) {
					return "";
				}
				return (
					<AgGridTooltipWrapper className="min-w-[224px] max-w-[320px] max-h-[320px]">
						{e.data?.description?.date && (
							<Row className="mb-2" gap={1}>
								<Icon icon="Clock" color={themeCSSVars.palette_P400} size={16} />
								<Text type="Body/S/Book">Last description update {formatDate(e.data.description.date)}</Text>
							</Row>
						)}
						<MarkdownRenderer>{e.value}</MarkdownRenderer>
					</AgGridTooltipWrapper>
				);
			},
			...coalesce(opt, {}),
		};
	},
	entityStatus<T>(
		field: ColDefField<T, InvestmentStatus | undefined>,
		opt?: Omit<ColumnOverrideParams<T, InvestmentStatus | undefined>, "cellRenderer">,
	): ColDef<T, InvestmentStatus | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? "Status",
			colId: opt?.colId ?? field,
			cellRenderer(row: CustomCellRendererProps<T, InvestmentStatus | undefined>) {
				if (row.value == null) {
					return "";
				}
				const map = statusIconMap[row.value];
				return (
					<Row className="h-full w-full" alignItems="center">
						<Icon
							className={{ "mr-1": true, "rotate-180": row.value === "ERROR" }}
							icon={map.icon}
							color={themeCSSVars.palette_N800}
							size={map.size}
						/>
						<Text type="Body/M/Book" className="line-clamp-2">
							{map.title}
						</Text>
					</Row>
				);
			},
			valueFormatter(row) {
				if (row.value == null) {
					return "";
				}
				const newValue = row.value;
				const map = statusIconMap[newValue];
				return map.title;
			},
			...coalesce(opt, {}),
		};
	},
	instrumentName<
		T extends {
			instrumentType: "INVESTMENT" | "INSTRUMENT";
			name?: string;
			nofInstruments?: number;
			investmentUuid?: string;
			macroAssetClassExposure?: InvestmentExposureEntry[];
			macroAssetClassExposureEnhanced?: InvestmentExposureEntry[];
			action?: InvestmentActions;
			lastActionNote?: InvestmentNote;
		},
	>(opt?: Omit<ColumnOverrideParams<T>, "cellRenderer" | "tooltipComponent">): ColDef<T> {
		return {
			headerName: opt?.headerName ?? "Name",
			colId: opt?.colId,
			field: "name" satisfies keyof T as any,
			cellRenderer(row: CustomCellRendererProps<T>) {
				if (row.data?.name == null) {
					return "";
				}

				if (row.data.instrumentType === "INVESTMENT") {
					return (
						<InvestmentName
							name={row.data.name}
							url={{
								target: "PortfolioDetails",
								params: { portfolioUid: row.data.investmentUuid!, tab: PortfolioDetailsTabs.COMPOSITION },
							}}
						/>
					);
				}

				return row.data.name ?? `Missing instrument name`;
			},
			tooltipField: "name" satisfies keyof T as any,
			tooltipComponent(params: CustomTooltipProps<T>) {
				if (
					params.data?.instrumentType === "INSTRUMENT" &&
					(params.data?.macroAssetClassExposure == null || params.data?.macroAssetClassExposure.length === 0)
				) {
					return <AgGridTooltipWrapper className="w-[224px]">{params.data.name}</AgGridTooltipWrapper>;
				}

				return (
					<InvestmentTooltip
						lastActionNote={params.data?.lastActionNote}
						name={params.data?.name}
						macroAssetClassExposure={params.data?.macroAssetClassExposure}
						macroAssetClassExposureEnhanced={params.data?.macroAssetClassExposureEnhanced}
					/>
				);
			},
			...coalesce(opt, {}),
		};
	},
	instrumentReviewStatus<T extends ReviewGridRow = ReviewInstrument>(
		opt?: Omit<ColumnOverrideParams<T>, "cellRenderer">,
	): ColDef<T> {
		const colDef = instrumentIssueColdDef();
		return {
			headerName: "Issues",
			colId: "ISSUE",
			cellRenderer: colDef.cellRenderer,
			onCellClicked(params) {
				const row = params.data;
				if (!row || !row.issue || row.deleted || isRowBlocked(row)) {
					return;
				}

				if (
					row.issue === "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW" ||
					row.issue === "MISSING_RISK_MODEL" ||
					row.issue === "NOT_SUPPORTED_BOND" ||
					row.issue === "MISSING_CLOSES"
				) {
					spawnInstrumentClassificationDialog({
						defaultInstrumentProxies: params.data?.proxies ?? [],
						onSubmitAsync({ indices, weights }) {
							const proxyIndexes = indices.map(
								(props): IndexTicker => ({
									...props,
									weight: props.ticker ? weights.get(props.ticker)?.toNumber() : undefined,
								}),
							);
							const columnId = params.column.getColId();
							if (columnId) {
								params.node.setDataValue(columnId, {
									proxies: proxyIndexes,
									hasBeenReviewed: true,
									newAlias: undefined,
								});
							}
						},
					}).catch(noop);
					return;
				}

				if (row.issue === "UNRECOGNIZED_INSTRUMENT") {
					spawnUnrecognizedInstrumentDialog({
						instrument: row.newAlias ?? row.alias ?? "",
						onSubmit(fields) {
							if (fields.strategy === "NEW_IDENTIFIER") {
								const columnId = params.column.getColId();
								if (columnId) {
									params.node.setDataValue(columnId, {
										proxies: [],
										hasBeenReviewed: true,
										newAlias: fields.identifier,
									});
								}
								return;
							}

							if (fields.strategy === "PROVIDE_RISK_MODEL") {
								spawnInstrumentClassificationDialog({
									defaultInstrumentProxies: row.proxies ?? [],
									onSubmitAsync: ({ indices, weights }) => {
										const proxyIndexes = indices.map(
											(props): IndexTicker => ({
												...props,
												weight: props.ticker ? weights.get(props.ticker)?.toNumber() : undefined,
											}),
										);
										const columnId = params.column.getColId();
										if (columnId) {
											params.node.setDataValue(columnId, {
												proxies: proxyIndexes,
												hasBeenReviewed: true,
												newAlias: undefined,
											});
										}
									},
								}).catch(noop);
							}
						},
					});
				}
			},
			valueGetter(params) {
				return {
					newAlias: params.data?.newAlias,
					proxies: params.data?.proxies,
					hasBeenReviewed: params.data?.hasBeenReviewed,
				};
			},
			valueSetter: (row) => {
				row.data.proxies = row.newValue.proxies;
				row.data.newAlias = row.newValue.newAlias;
				row.data.hasBeenReviewed = row.newValue.hasBeenReviewed;
				return true;
			},
			lockVisible: true,
			...coalesce(opt, {}),
		};
	},
	instrumentCustomisationActions<T>(
		opt?: InstrumentActionsOverrides<T> & {
			onExport?: () => void;
		},
	): ColDef<T> {
		const { onExport, ...restOpt } = coalesce(opt, {} as NonNullable<typeof opt>);
		return instrumentActionsColDef(actionColumnsByArea().instrumentCustomisation<T>({ onExport }), restOpt);
	},
	instrumentCompositionEditActions<T extends { deleted?: boolean }>(opt?: InstrumentActionsOverrides<T>): ColDef<T> {
		return instrumentActionsColDef(actionColumnsByArea().instrumentCompositionEdit<T>(), opt);
	},
	instrumentQuantityCompositionEditActions<T extends { deleted?: boolean; isCash?: boolean }>(
		opt?: InstrumentActionsOverrides<T>,
	): ColDef<T> {
		return instrumentActionsColDef(actionColumnsByArea().instrumentQuantityCompositionEdit<T>(), opt);
	},
	instrumentCompositionReadActions<T>(opt?: InstrumentActionsOverrides<T>): ColDef<T> {
		return instrumentActionsColDef(actionColumnsByArea().instrumentCompositionRead<T>(), opt);
	},
	instrumentReviewActions<T extends { issue?: ReviewTickerIssueEnum; delisted?: boolean; deleted?: boolean }>(
		opt?: InstrumentActionsOverrides<T>,
	): ColDef<T> {
		return instrumentActionsColDef(actionColumnsByArea().instrumentReview<T>(), opt);
	},
	instrumentDeltaWeight<T extends { weight?: number; previousWeight?: number }>(
		opt?: Omit<ColumnOverrideParams<T>, "cellRenderer" | "valueGetter">,
	): ColDef<T> {
		return {
			headerName: "Delta",
			cellRenderer(params: CustomCellRendererProps<T>) {
				const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
				if (params.node.group) {
					const groupWeight = sumArrayLike(params.node.allLeafChildren ?? [], (x) => x.data?.weight ?? 0);
					const groupPreviousWeight = sumArrayLike(
						params.node.allLeafChildren ?? [],
						(x) => x.data?.previousWeight ?? 0,
					);

					const deltaWeight = Number(groupWeight - groupPreviousWeight);
					return (
						<Row className="h-full w-full" alignItems="center">
							<InfoDelta
								diff={Number.isFinite(deltaWeight) ? deltaWeight : 0}
								enh={groupWeight ?? 0}
								decimalPlaces={decimalPlaces}
							/>
						</Row>
					);
				}

				if (params.data == null) {
					return "";
				}

				const deltaWeight = Number((params.data.weight ?? 0) - (params.data.previousWeight ?? 0));
				return (
					<Row className="h-full w-full" alignItems="center">
						<InfoDelta
							diff={Number.isFinite(deltaWeight) ? deltaWeight : 0}
							enh={params.data.weight ?? 0}
							decimalPlaces={decimalPlaces}
						/>
					</Row>
				);
			},
			tooltipValueGetter(params) {
				if (params.data == null) {
					return null;
				}
				const delta = (params.data.weight ?? 0) - (params.data.previousWeight ?? 0);
				// Unlike the weight columns — whose tooltip shows a stored value verbatim — this one shows a
				// difference, so full precision would only expose double-subtraction artifacts
				// (50.1 - 48.7 === 1.4000000000000057). Round it like the cell does instead.
				const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
				return `${formatNumberWithBankersRounding(delta, decimalPlaces)}%`;
			},
			valueGetter(params) {
				if (params.data == null) {
					return "";
				}
				const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
				const formattedValue = formatNumberWithBankersRounding(
					(params.data.weight ?? 0) - (params.data.previousWeight ?? 0),
					decimalPlaces,
				);
				return formattedValue;
			},
			...coalesce(opt, {}),
		};
	},
	investableUniverseStatus<T extends { additional?: boolean; strategy?: InvestableUniverseSelectionStrategy }>(
		opt?: ColumnOverrideParams<T>,
	): ColDef<T> {
		return {
			headerName: "Status",
			field: "additional" as any,
			cellRenderer(params: CustomCellRendererProps<T>) {
				if (!params.data?.additional) {
					return "";
				}

				if (params.data.strategy === "SELL_INSTRUMENTS") {
					return (
						<Row className="h-full w-full" alignItems="center">
							<Badge color={themeCSSVars.palette_N20} backgroundColor="#A0A7B6">
								{t("EXCLUDE")}
							</Badge>
						</Row>
					);
				}

				return (
					<Row className="h-full w-full" alignItems="center">
						<Badge color={themeCSSVars.palette_N20} backgroundColor="#A0A7B6">
							{t("NEW", {})}
						</Badge>
					</Row>
				);
			},
			...coalesce(opt, {}),
		};
	},
	riskModel<T extends { riskModel?: Array<RichRiskModelValue> }>(
		area: "instrumentCustomisation" | "instrumentCompositionRead" = "instrumentCompositionRead",
		opt?: ColumnOverrideParams<T, Array<RichRiskModelValue>>,
	): ColDef<T, Array<RichRiskModelValue>> {
		return riskModelColDef({
			editable: area === "instrumentCustomisation",
			...opt,
		});
	},
	instrumentCompositionWeight<T extends Instrument = Instrument>(
		opt?: Omit<ColumnOverrideParams<T, number | undefined>, "colId" | "cellRenderer" | "valueSetter">,
	): ColDef<T, number | undefined> {
		return sphereColumnsDef.percentage<T>(instrumentField<T>("weight"), {
			headerName: "Weight",
			colId: "WEIGHT",
			aggFunc: "sumWeights",
			cellEditorParams: {
				min: 0,
				max: 100,
				precision: 16,
			},
			tooltipValueGetter(params) {
				if (typeof params.value !== "number") {
					return null;
				}
				return `${formatNumber(params.value, { maxDecimalPlaces: 16, minDecimalPlaces: 0 })}%`;
			},
			valueFormatter(params) {
				const user = readAtom(user$);
				const decimalPlaces = user.userDecimalPlacesPreferences;
				if (params.colDef.editable) {
					if (params.node?.rowPinned === "bottom" && params.node.footer) {
						return formatWeightTotal({
							total: params.value ?? 0,
							displayDecimals: decimalPlaces ?? 2,
							precision: customerDecimalPlaces(user),
						});
					}
					return `${formatNumber(params.value ?? 0, decimalPlaces)}%`;
				}
				return `${formatNumberWithBankersRounding(params.value ?? 0, decimalPlaces)}%`;
			},
			// cellRenderer(e: CustomCellRendererProps<Instrument, number | undefined>) {
			// 	const start = e.valueFormatted?.substring(0, 5);
			// 	const end = e.valueFormatted?.substring(5);
			// 	return (
			// 		<Text type="Body/M/Book" as="span">
			// 			<Text type="Body/M/Bold">{start ?? ""}</Text>
			// 			{end ?? ""}
			// 		</Text>
			// 	);
			// },
			valueGetter(params) {
				if (params.node?.group) {
					const sum = sumArrayLike(params.node.allLeafChildren ?? [], (row) => {
						if (row.data?.weight == null) {
							return 0;
						}
						return row.data?.deleted ? 0 : row.data.weight;
					});
					return sum;
				}

				if (params.data?.deleted) {
					return 0;
				}
				return params.data?.weight ?? 0;
			},
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
				"font-semibold": (params) => Boolean(params.node.group),
			},
			...(opt ?? {}),
			editable(params) {
				if (opt?.editable) {
					return !params.data?.deleted;
				}

				return !!opt?.editable;
			},
		});
	},
	/**
	 * Quantity-mode (MDOT-24700) column: number of shares. Editable integer (truncation to a
	 * whole quantity happens in the builder's edit resolution, not here). The cash row has no
	 * quantity — it renders "—" and is never editable.
	 */
	instrumentQuantity<T extends Instrument = Instrument>(
		opt?: Omit<ColumnOverrideParams<T, number | undefined>, "colId" | "valueSetter">,
	): ColDef<T, number | undefined> {
		return sphereColumnsDef.integer<T>(instrumentField<T>("quantity"), {
			headerName: "Quantity",
			colId: "QUANTITY",
			cellEditor: "agNumberCellEditor",
			cellEditorParams: {
				min: 0,
				step: 1,
				precision: 0,
			},
			valueGetter(params) {
				if (params.node?.group || !params.data || params.data.isCash) {
					return undefined;
				}
				return params.data.quantity;
			},
			valueFormatter(params) {
				if (params.data?.isCash) {
					return "—";
				}
				if (params.value == null) {
					return "";
				}
				return formatNumber(params.value, 0);
			},
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
				"ag-cell--monospace-font": () => true,
			},
			minWidth: 120,
			...(opt ?? {}),
			editable(params) {
				if (!opt?.editable) {
					return false;
				}
				// `data == null` covers group and pinned-total rows, which must never edit.
				return params.data != null && !params.data.isCash && !params.data.deleted;
			},
		});
	},
	/**
	 * Quantity-mode (MDOT-24700) column: unit market price, backend-provided and read-only.
	 * The cash row renders "—".
	 */
	instrumentMarketPrice<T extends Instrument = Instrument>(
		opt?: Omit<ColumnOverrideParams<T, number | undefined>, "colId" | "valueSetter" | "editable">,
	): ColDef<T, number | undefined> {
		return sphereColumnsDef.floating<T>(instrumentField<T>("price"), {
			headerName: "Market price",
			colId: "MARKET_PRICE",
			editable: false,
			valueGetter(params) {
				if (params.node?.group || !params.data || params.data.isCash) {
					return undefined;
				}
				return params.data.price;
			},
			valueFormatter(params) {
				if (params.data?.isCash) {
					return "—";
				}
				if (params.value == null) {
					return "";
				}
				return formatNumber(params.value, 2);
			},
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
				"ag-cell--monospace-font": () => true,
			},
			minWidth: 120,
			...(opt ?? {}),
		});
	},
	/**
	 * Quantity-mode (MDOT-24700) column: RENDER-ONLY market value read straight off
	 * `row.marketValue` — never computed here. The value is backend-provided on read-only
	 * compositions and stored by the quantity editor on every edit: Cold War's sizing for the
	 * edited row (`quantity/position-sizing`), the AUM remainder for the cash row
	 * (`quantity/derive`), which may be negative. Instrument rows
	 * display rounded to 2 dp, the same as the Market price column, so a quantity of 1 reads
	 * identically in both cells; the pinned grand total displays through
	 * `truncateToPrecision(v, 2)` so a not-fully-allocated total never shows rounded up.
	 * `params.currency` shows as its ISO code in the header; every cell, the grand total included, is a plain number.
	 */
	instrumentMarketValue<T extends Instrument = Instrument>(
		params: { currency?: Currencies | null },
		opt?: Omit<ColumnOverrideParams<T, number | undefined>, "colId" | "valueSetter">,
	): ColDef<T, number | undefined> {
		return {
			headerName: `Market value (${currencyCode(params.currency)})`,
			colId: "MARKET_VALUE",
			cellDataType: "floating",
			cellEditor: "agNumberCellEditor",
			cellEditorParams: {
				min: 0,
			},
			// The value is derived, never written to the row. This accept-only setter is a
			// fallback that keeps AG Grid from rejecting the edit outright; the quantity editor
			// overrides it with a committing setter (`withQuantityCommitHandlers`) because AG Grid
			// re-reads the cell after the setter and fires onCellValueChanged with the re-read
			// (i.e. old) value — the typed value is only available inside the valueSetter.
			valueSetter(setterParams) {
				return setterParams.newValue != null;
			},
			aggFunc: "sum",
			valueGetter(getterParams) {
				const row = getterParams.data;
				if (!row) {
					return undefined;
				}
				// Deleted rows render 0 so the pinned grand total (aggFunc sum) excludes them.
				if (row.deleted) {
					return 0;
				}
				return row.marketValue;
			},
			valueFormatter(formatterParams) {
				if (formatterParams.value == null) {
					return "";
				}
				// Grand-total footer only — the pinned cash COPY is also bottom-pinned but never a footer.
				const isGrandTotal = formatterParams.node?.rowPinned === "bottom" && Boolean(formatterParams.node.footer);
				if (isGrandTotal) {
					return formatNumber(truncateToPrecision(formatterParams.value, 2), 2);
				}
				// Instrument rows round, matching the Market price column: at a quantity of 1 the two
				// cells read the same value.
				return formatNumber(formatterParams.value, 2);
			},
			cellClassRules: {
				"opacity-50": (cellParams) => Boolean(cellParams.node.data?.deleted),
				"font-semibold": (cellParams) => Boolean(cellParams.node.group),
				"ag-cell--monospace-font": () => true,
			},
			minWidth: 140,
			...(opt ?? {}),
			editable(editableParams) {
				if (!opt?.editable) {
					return false;
				}
				// `data == null` covers group and pinned-total rows, which must never edit.
				return editableParams.data != null && !editableParams.data.isCash && !editableParams.data.deleted;
			},
		};
	},
	investmentName<T extends { uuid?: string; name?: string; status?: string; action?: string; richAcl?: RichAcl }>(
		compare?: CompareInvestmentProps,
		opt?: ColumnOverrideParams<T>,
		disableLink?: boolean,
	): ColDef<T> {
		type InvestmentNameValue = { name?: string; richAcl?: RichAcl; status?: string };

		const forwarding = (row: T) =>
			disableLink
				? { onClick: noop }
				: {
						href: match(row)
							.when(
								(investment) => investment.status === "DRAFT" && investment.action === "UPLOAD",
								() => typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid: row.uuid! }),
							)
							.when(
								(investment) => investment.status === "DRAFT" && investment.action === "CREATION",
								() => typedUrlForRoute("Portfolios/Draft", { portfolioUid: row.uuid! }),
							)
							.when(
								(investment) => investment.status === "REVIEW",
								() => typedUrlForRoute("ReviewInstruments", { uuid: row.uuid!, entity: ReviewEntity.INVESTMENT }),
							)
							.otherwise(() => typedUrlForRoute("PortfolioDetails", { portfolioUid: row.uuid! })),
					};
		return {
			headerName: "Name",
			colId: "NAME",
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;
				if (!row) {
					return "";
				}
				const { richAcl, name } = row;
				const props = forwarding(row);
				return (
					<Row className="h-full w-full" alignItems="center">
						<ActionText
							className="line-clamp-2 mr-1"
							color={themeCSSVars.palette_N900}
							{...props}
							title={name}
							data-qualifier="PortfolioStudio/ReferenceList/Table/Column(Name)"
						>
							{name ?? ""}
						</ActionText>
						<WithAtom atom$={user$}>
							{(user) => (
								<UserShareIcon
									entity="INVESTMENT"
									userId={user.id}
									permission={richAcl?.currentUserPermissions}
									color={themeCSSVars.palette_N300}
									acl={richAcl?.acl}
								/>
							)}
						</WithAtom>
					</Row>
				);
			},
			valueGetter(params): InvestmentNameValue {
				return { name: params.data?.name, richAcl: params.data?.richAcl, status: params.data?.status };
			},
			// Clipboard and export read the formatted value, so a copied cell yields the name, not the projection.
			valueFormatter(params: ValueFormatterParams<T, InvestmentNameValue>) {
				return params.value?.name ?? "";
			},
			equals(valueA: InvestmentNameValue | null | undefined, valueB: InvestmentNameValue | null | undefined) {
				return (
					valueA?.name === valueB?.name &&
					instanceEqualsOrFastDeepEqual(valueA?.richAcl, valueB?.richAcl) &&
					valueA?.status === valueB?.status
				);
			},
			tooltipValueGetter(params) {
				return params.data?.name;
			},
			tooltipComponent(params: CustomTooltipProps<T>) {
				const row = params.data;

				if (
					row?.status === "ERROR" ||
					row?.status === "CALCULATING" ||
					row?.status === "REVIEW" ||
					row?.status === "DRAFT" ||
					row?.status === "RETRIEVING_DATA" ||
					compare == null ||
					!row?.uuid
				) {
					return "";
				}

				return (
					<ReactQueryWrapper {...compare.queryConfig(row?.uuid)} errorFallback="" loadingFallback="...">
						{({ lastActionNote, macroAssetClassExposure, macroAssetClassExposureEnhanced }) => (
							<InvestmentTooltip
								lastActionNote={lastActionNote}
								name={row.name ?? "Missing"}
								macroAssetClassExposure={macroAssetClassExposure}
								macroAssetClassExposureEnhanced={macroAssetClassExposureEnhanced}
							/>
						)}
					</ReactQueryWrapper>
				);
			},
			...(opt ?? {}),
		};
	},
	investmentAutoSync<
		T extends { importedExternally?: boolean; syncDate?: string; importedPerformances?: ImportedPerformances },
	>(opt?: ColumnOverrideParams<T>): ColDef<T> {
		return {
			headerName: t(`TABLE.HEADERS.AUTO_SYNC`),
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;
				if ((row?.importedExternally && row?.syncDate) || row?.importedPerformances?.lastImportDate) {
					return (
						<Row className="h-full w-full" alignItems="center">
							<AutoSyncStatus
								variant="compat"
								syncDate={row.syncDate}
								lastMetricImportDate={row.importedPerformances?.lastImportDate}
							/>
						</Row>
					);
				}

				return "";
			},
			...(opt ?? {}),
		};
	},
	investmentMarketView<T extends { marketView?: MarketViewMonitoringMetric; uuid?: string }>(
		opt?: ColumnOverrideParams<T>,
	): ColDef<T> {
		return {
			headerName: t(`TABLE.HEADERS.MARKET_VIEW_NAME`),
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;

				if (!row?.marketView) {
					return "";
				}

				return (
					<Row className="h-full w-full" alignItems="center">
						<InvestmentName
							name={row.marketView.scenarioName ?? `Market view ${formatDate(row.marketView.forecastDates?.from)}`}
							url={match(row)
								.returnType<RouteProps<"MarketViewWorkSpace/View"> | RouteProps<"PortfolioDetails">>()
								.when(
									(x) => x.marketView?.scenarioIdentifier,
									(x) => ({
										target: "MarketViewWorkSpace/View",
										params: { uuid: x.marketView!.scenarioIdentifier! },
									}),
								)
								.otherwise((x) => ({
									target: "PortfolioDetails",
									params: {
										portfolioUid: x.uuid!,
										tab: `${PortfolioDetailsTabs.PORTFOLIO_STUDIO_SETTINGS}#marketView`,
									},
								}))}
						/>
					</Row>
				);
			},
			valueFormatter(params) {
				if (params.data == null) {
					return "";
				}

				const row = params.data;
				return row.marketView?.scenarioName ?? `Market view ${formatDate(row.marketView?.forecastDates?.from)}`;
			},
			...(opt ?? {}),
		};
	},
	investmentType<T extends { liveTrading?: boolean }>(opt?: ColumnOverrideParams<T>): ColDef<T> {
		return {
			headerName: t(`TABLE.HEADERS.PORTFOLIO_TYPE`),
			field: "liveTrading" as any,
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;
				return row?.liveTrading ? t(`PORTFOLIOS.PORTFOLIO_TYPE_LIVE`) : t(`PORTFOLIOS.PORTFOLIO_TYPE_PAPER`);
			},
			valueFormatter(params) {
				if (params.data == null) {
					return "";
				}

				const row = params.data;
				return row?.liveTrading ? t(`PORTFOLIOS.PORTFOLIO_TYPE_LIVE`) : t(`PORTFOLIOS.PORTFOLIO_TYPE_PAPER`);
			},
			...(opt ?? {}),
		};
	},
	investmentWeightingMethod<T extends { fixedWeights?: boolean }>(opt?: ColumnOverrideParams<T>): ColDef<T> {
		return {
			headerName: t(`TABLE.HEADERS.WEIGHTING_METHOD`),
			field: "fixedWeights" as any,
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;
				return row?.fixedWeights ? "Fixed" : "Drifting";
			},
			valueFormatter(params) {
				if (params.data == null) {
					return "";
				}

				const row = params.data;
				return row?.fixedWeights ? "Fixed" : "Drifting";
			},
			...(opt ?? {}),
		};
	},
	investmentKeyMetric<T extends { metrics?: string }>(opt?: ColumnOverrideParams<T>): ColDef<T, string | undefined> {
		return sphereColumnsDef.string<T>("metrics" as any, {
			colId: "KEY_METRIC",
			valueFormatter(x) {
				if (!x.value) {
					return t("NOT_AVAILABLE_DATA");
				}
				return x.value;
			},
			pinned: "left",
			width: 168,
			headerValueGetter(params: HeaderValueGetterParams<T, any, { keyMetricHeaderName?: string }>) {
				const contextHeaderName = params.context.keyMetricHeaderName;
				return contextHeaderName ?? "Key metric";
			},
			...(opt ?? {}),
		});
	},
	referenceInvestmentName<
		T extends {
			name?: string;
			uuid?: string;
			type: "References" | "Benchmark" | "LegacyBenchmark";
			richAcl?: RichAcl;
			status?: InvestmentStatus;
		},
	>(opt?: ColumnOverrideParams<T>): ColDef<T, string | undefined> {
		return {
			...commonColumnsDef.string("name" satisfies keyof T as any),
			colId: "NAME",
			headerName: "Name",
			pinned: "left",
			width: 320,
			sort: "asc",
			cellRenderer(row: CustomCellRendererProps<T>) {
				if (!row.data) {
					return "";
				}

				const { name, richAcl } = row.data;
				return (
					<Row className="h-full w-full" alignItems="center">
						<ActionText
							className="line-clamp-2 mr-1"
							color={themeCSSVars.palette_N900}
							href={match(row.data)
								.when(
									(x) => x.status === "REVIEW" && (x.type === "Benchmark" || x.type === "References"),
									(x) =>
										typedUrlForRoute("ReviewInstruments", { entity: ReviewEntity.TARGET_INVESTMENT, uuid: x.uuid! }),
								)
								.when(
									/***
									 * @deprecated user cannot edit anymore an legacy benchmark
									 */
									(x) => x.status === "REVIEW" && x.type === "LegacyBenchmark",
									(x) => typedUrlForRoute("ReviewInstruments", { entity: ReviewEntity.BENCHMARK, uuid: x.uuid! }),
								)
								.when(
									(x) => x.status !== "REVIEW" && (x.type === "Benchmark" || x.type === "References"),
									(x) => typedUrlForRoute("ReferenceDetails", { uuid: x.uuid! }),
								)
								.when(
									(x) => x.status !== "REVIEW" && x.type === "LegacyBenchmark",
									(x) => typedUrlForRoute("LegacyBenchmark", { uuid: x.uuid! }),
								)
								.otherwise(() => getPortfolioStudioUrl())}
							title={name}
							data-qualifier="PortfolioStudio/ReferenceList/Table/Column(Name)"
						>
							{name ?? ""}
						</ActionText>
						<WithAtom atom$={user$}>
							{(user) => (
								<UserShareIcon
									entity="MARKET_VIEW"
									userId={user.id}
									permission={richAcl?.currentUserPermissions}
									color={themeCSSVars.palette_N300}
									acl={richAcl?.acl}
								/>
							)}
						</WithAtom>
					</Row>
				);
			},
			...(opt ?? {}),
		};
	},
	referenceInvestmentLabel<T extends { type: ReferenceItem["type"] }>(
		opt?: ColumnOverrideParams<T>,
	): ColDef<T, ReferenceItem["type"]> {
		return {
			headerName: t("TABLE.HEADERS.LABEL"),
			colId: "TYPE",
			field: "type" satisfies keyof T as any,
			cellRenderer(params: CustomCellRendererProps<T>) {
				const row = params.data;
				if (!row) {
					return "";
				}
				return t(`REFERENCE.TYPE.${row.type}`);
			},
			width: 128,
			...(opt ?? {}),
		};
	},
	workspaceNumber<T>(
		field: ColDefField<T, { value?: number; message?: string }>,
		fieldOptions: {
			decimalPlaces: number | Pick<FormatNumberOptions, "minDecimalPlaces" | "maxDecimalPlaces">;
			percentage: boolean;
			hideMissingMessage?: boolean;
		},
		columnOptions?: ColumnOverrideParams<T, { value?: number; message?: string }>,
	): ColDef<T, { value?: number; message?: string }> {
		const decimalPlaces: Partial<FormatNumberOptions> =
			typeof fieldOptions.decimalPlaces === "number"
				? { minDecimalPlaces: fieldOptions.decimalPlaces, maxDecimalPlaces: fieldOptions.decimalPlaces }
				: fieldOptions.decimalPlaces;
		return {
			field,
			headerName: columnOptions?.headerName ?? field,
			colId: columnOptions?.colId ?? field,
			cellDataType: "number",
			cellRenderer(row: CustomCellRendererProps<T, { value?: number; message?: string }>) {
				const cellData = (row.data as any)?.[field as string] as { value?: number; message?: string } | undefined;
				const value = cellData?.value;
				const message = cellData?.message;
				if (value == null) {
					if (fieldOptions.hideMissingMessage) {
						return "";
					}

					return (
						<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={4}>
							<Text type="Body/M/Book">N/A</Text>
							{message && (
								<IconTooltip severity="info" overrideIconColor={themeCSSVars.palette_N700} iconSize={16} hideArrow>
									{message}
								</IconTooltip>
							)}
						</Row>
					);
				}

				return `${formatNumber(value, decimalPlaces)}${fieldOptions.percentage ? "%" : ""}`;
			},

			valueGetter: (params) => (params.data as any)?.[field as string]?.value ?? undefined,
			valueFormatter: (params) =>
				params.value == null
					? ""
					: `${formatNumber(params.value as number, decimalPlaces)}${fieldOptions.percentage ? "%" : ""}`,
			valueSetter: (params) => {
				const numericValue = Number(params.newValue);
				if (Number.isNaN(numericValue)) {
					return false;
				}
				(params.data as any)[field] = { value: numericValue || undefined };
				return true;
			},
			...coalesce(columnOptions, {}),
		};
	},
	workspaceStatus<T>(
		field: ColDefField<T, WorkspaceDataCast.OptionsProvider<"STATUS"> | undefined>,
		opt?: Omit<ColumnOverrideParams<T, WorkspaceDataCast.OptionsProvider<"STATUS"> | undefined>, "cellRenderer">,
	): ColDef<T, WorkspaceDataCast.OptionsProvider<"STATUS"> | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? "Status",
			colId: opt?.colId ?? field,
			cellRenderer(row: CustomCellRendererProps<T, WorkspaceDataCast.OptionsProvider<"STATUS"> | undefined>) {
				if (row.value == null) {
					return "";
				}
				const remappedStatus = row.value === "RETRIEVING_DATA" ? "RETRIEVING_DATA" : row.value;

				const map = statusIconMap[remappedStatus];
				return (
					<Row className="h-full w-full" alignItems="center">
						<Icon
							className={{ "mr-1": true, "rotate-180": row.value === "ERROR" }}
							icon={map.icon}
							color={themeCSSVars.palette_N800}
							size={map.size}
						/>
						<Text type="Body/M/Book" className="line-clamp-2">
							{map.title}
						</Text>
					</Row>
				);
			},
			valueFormatter(row) {
				if (row.value == null) {
					return "";
				}
				const newValue = row.value;
				const remappedStatus = newValue === "RETRIEVING_DATA" ? "RETRIEVING_DATA" : newValue;
				const map = statusIconMap[remappedStatus];
				return map?.title;
			},
			...coalesce(opt, {}),
		};
	},
	workspaceSharingInfo<T>(
		field: ColDefField<T, WorkspaceDataCast.OptionsProvider<"SHARING_INFO"> | undefined>,
		opt?: Omit<ColumnOverrideParams<T, WorkspaceDataCast.OptionsProvider<"SHARING_INFO"> | undefined>, "cellRenderer">,
	): ColDef<T, WorkspaceDataCast.OptionsProvider<"SHARING_INFO"> | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? "Info",
			colId: opt?.colId ?? field,
			cellRenderer(row: CustomCellRendererProps<T, WorkspaceDataCast.OptionsProvider<"SHARING_INFO"> | undefined>) {
				if (row.value === undefined) {
					return "";
				}

				const users = aclToUsersListEntries("portfolio", row.value?.acl ?? []);
				return (
					<Row className="h-full w-full" alignItems="center">
						<UsersList users={users} visibleSlots={3} size="small" />
					</Row>
				);
			},
			valueFormatter(row) {
				const users = aclToUsersListEntries("portfolio", row.value?.acl ?? []);
				if (users.length === 0) {
					return "";
				}
				return users.map((x) => x.firstName + " " + x.lastName + "(" + x.role + ")").join(" , ");
			},
			...coalesce(opt, {}),
		};
	},
	workspaceEntity<T extends MandatoryItemData>(
		field: ColDefField<T, WorkspaceDataCast.OptionsProvider<"SPHERE_ENTITY"> | undefined>,
		opt?: Omit<ColumnOverrideParams<T, WorkspaceDataCast.OptionsProvider<"SPHERE_ENTITY"> | undefined>, "cellRenderer">,
		workspace?: string,
	): ColDef<T> {
		type EntityValue = WorkspaceDataCast.OptionsProvider<"SPHERE_ENTITY"> | undefined;
		type EntityValueWithAcl = { entity: EntityValue; richAcl: MandatoryItemDataRichAcl | undefined };
		return {
			headerName: opt?.headerName ?? "Entity",
			colId: opt?.colId ?? field,
			valueGetter(params): EntityValueWithAcl {
				return {
					entity: params.data?.[field as keyof T] as EntityValue,
					richAcl: params.data?.richAcl,
				};
			},
			equals(valueA, valueB: EntityValueWithAcl | null | undefined) {
				return (
					instanceEqualsOrFastDeepEqual(valueA?.entity, valueB?.entity) &&
					instanceEqualsOrFastDeepEqual(valueA?.richAcl, valueB?.richAcl)
				);
			},
			cellRenderer(row: CustomCellRendererProps<T, EntityValueWithAcl | undefined>) {
				const compositeValue = row.value;
				const value = compositeValue?.entity;
				// TODO: investigate why sometimes row.value is an empty object (requiring us explicitly check for the type subproperty)
				if (value?.type == null) {
					return "";
				}
				const isValidUuid = Result.fromThrower(() => z.string().uuid().parse(value.uuid)).isOk();

				if (!isValidUuid || !row.data) {
					return (
						// if the want i can redirect to investment but i have to intersect some data
						<Row className="h-full w-full" alignItems="center">
							<Text
								className="line-clamp-2 mr-1"
								color={themeCSSVars.palette_N900}
								type="Body/M/Book"
								data-qualifier="PortfolioStudio/ReferenceList/Table/Column(Name)"
							>
								{value.name ?? ""}
							</Text>
							<WithAtom atom$={user$}>
								{(user) => (
									<UserShareIcon
										entity="INVESTMENT"
										userId={user.id}
										color={themeCSSVars.palette_N300}
										acl={row.data?.richAcl?.acl}
										permission={row.data?.richAcl?.currentUserPermissions}
									/>
								)}
							</WithAtom>
						</Row>
					);
				}

				return (
					<Row className="h-full w-full" alignItems="center">
						<ActionText
							className="line-clamp-2 mr-1"
							color={themeCSSVars.palette_N900}
							disabled={!isValidUuid}
							href={switchExpr(value.type, {
								INVESTMENT: () =>
									match(row.data!)
										.when(
											(investment) => investment.status === "DRAFT" && investment.lastAction === "UPLOAD",
											(investment) =>
												typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid: investment.uuid! }),
										)
										.when(
											(investment) => investment.status === "DRAFT" && investment.lastAction === "CREATION",
											(investment) => typedUrlForRoute("Portfolios/Draft", { portfolioUid: investment.uuid! }),
										)
										.when(
											(investment) => investment.status === "REVIEW",
											(investment) =>
												typedUrlForRoute("ReviewInstruments", {
													uuid: investment.uuid!,
													entity: ReviewEntity.INVESTMENT,
												}),
										)
										.otherwise((investment) =>
											workspace
												? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
														workspaceUid: workspace,
														portfolioUid: investment.uuid!,
													})
												: typedUrlForRoute("PortfolioDetails", { portfolioUid: investment.uuid! }),
										),
								UNIVERSE: () => typedUrlForRoute("UniverseDetails", { universeUuid: value.uuid }), // TODO: missing whether it's "with groups" or a regular universe
								REFERENCE: () => typedUrlForRoute("ReferenceDetails", { uuid: value.uuid }),
								MARKET_VIEW: () => typedUrlForRoute("MarketViewWorkSpace/View", { uuid: value.uuid }),
								BENCHMARK: () => typedUrlForRoute("ReferenceDetails", { uuid: value.uuid }),
							})}
							data-qualifier="PortfolioStudio/ReferenceList/Table/Column(Name)"
						>
							{value.name ?? ""}
						</ActionText>
						<WithAtom atom$={user$}>
							{(user) => (
								<UserShareIcon
									entity="INVESTMENT"
									userId={user.id}
									color={themeCSSVars.palette_N300}
									acl={row.data?.richAcl?.acl}
									permission={row.data?.richAcl?.currentUserPermissions}
								/>
							)}
						</WithAtom>
					</Row>
				);
			},

			tooltipValueGetter(params) {
				return params.data?.name;
			},
			tooltipComponent(
				params: CustomTooltipProps<T & Record<string, WorkspaceDataCast.OptionsProvider<"SPHERE_ENTITY">>, string>,
			) {
				const row = params.data;
				const queryConfig = params.context.compareQueryConfig as
					| CompareInvestmentProps["queryConfig"]
					| null
					| undefined;

				const filedRowData = params.data?.[field];
				if (filedRowData?.type !== "INVESTMENT") {
					return "";
				}

				if (
					row?.status === "ERROR" ||
					row?.status === "CALCULATING" ||
					row?.status === "REVIEW" ||
					row?.status === "DRAFT" ||
					row?.status === "RETRIEVING_DATA" ||
					queryConfig == null ||
					!row?.uuid
				) {
					return "";
				}

				return (
					<ReactQueryWrapper {...queryConfig(row?.uuid)} errorFallback="" loadingFallback="...">
						{({ lastActionNote, macroAssetClassExposure, macroAssetClassExposureEnhanced }) => (
							<InvestmentTooltip
								lastActionNote={lastActionNote}
								name={row.name ?? "Missing"}
								macroAssetClassExposure={macroAssetClassExposure}
								macroAssetClassExposureEnhanced={macroAssetClassExposureEnhanced}
							/>
						)}
					</ReactQueryWrapper>
				);
			},
			valueFormatter(params) {
				return params.data?.name ?? "";
			},
			...coalesce(opt, {}),
		};
	},
	workspaceCustomAttributeTags<T>(
		field: ColDefField<T, Array<string>>,
		meta: TagSettings,
		opt?: Omit<ColumnOverrideParams<T, Array<string>>, "tooltipComponent" | "cellRenderer" | "cellEditor">,
	): ColDef<T, Array<string>> {
		return richTagColDef(field, meta, opt);
	},
	workspaceTags<T>(
		field: ColDefField<T, WorkspaceDataCast.OptionsProvider<"TAGS"> | undefined>,
		opt?: Omit<ColumnOverrideParams<T, WorkspaceDataCast.OptionsProvider<"TAGS"> | undefined>, "cellRenderer">,
	): ColDef<T, WorkspaceDataCast.OptionsProvider<"TAGS"> | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? "Status",
			colId: opt?.colId ?? field,
			cellRenderer(row: CustomCellRendererProps<T, WorkspaceDataCast.OptionsProvider<"TAGS"> | undefined>) {
				if (row.value == null) {
					return "";
				}

				return <>Missing impl</>;
			},
			...coalesce(opt, {}),
		};
	},
	workspaceSyncDate<T extends MandatoryItemData>(
		field: ColDefField<T, WorkspaceDataCast.OptionsProvider<"DATE"> | undefined>,
		syncType: "investment-sync" | "metrics-sync",
		opt?: Omit<ColumnOverrideParams<T, WorkspaceDataCast.OptionsProvider<"DATE"> | undefined>, "cellRenderer">,
	): ColDef<T, WorkspaceDataCast.OptionsProvider<"DATE"> | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? "Status",
			colId: opt?.colId ?? field,
			cellRenderer(row: CustomCellRendererProps<T, WorkspaceDataCast.OptionsProvider<"DATE"> | undefined>) {
				if (!row.value || typeof row.value === "object") {
					return "";
				}

				if (syncType === "investment-sync") {
					return (
						<Row className="h-full w-full" alignItems="center">
							<AutoSyncStatus variant="compat" syncDate={row.value} />
						</Row>
					);
				}

				return (
					<Row className="h-full w-full" alignItems="center">
						<AutoSyncStatus variant="compat" lastMetricImportDate={row.value} />
					</Row>
				);
			},
			valueFormatter(params) {
				if (!params.value || typeof params.value === "object") {
					return "";
				}
				return params.value;
			},
			...coalesce(opt, {}),
		};
	},
} as const;
